import Cookies from 'js-cookie'

import { runtimeConfig } from '@app/common/config'
import { isBrowser } from '@app/utils/isBrowser'
import { AB_TEST_DATA_VARIANT_COOKIE, BOOKING_FLOW_USER_ID_COOKIE } from '@app/constants'
import { segmentSSR } from './segmentSSR'
import { AbTestVariants } from '@app/store/ui'

const abTestCampaign = runtimeConfig.joinSproutTherapyConfig?.AB_TEST_IS_EXECUTING

const getAnalytics = () =>
  // @ts-ignore
  analytics?.Integrations
    ? {
        type: 'analytics-js',
        instance: analytics
      }
    : {
        type: 'analytics-node',
        instance: segmentSSR
      }

export const identifySegment = async (sessionId: string) => {
  if (!isBrowser) {
    return
  }

  getAnalytics().instance?.identify(sessionId)
}

export const trackSegmentEvent = async (
  sessionId: string,
  eventName: string,
  properties?: any,
  callback?: () => void
) => {
  if (eventName && isBrowser) {
    // TODO: remove this temp function to catch SP-8171 (when booking flow user id is null)
    tempFuncToCatchMissingId(eventName, Cookies.get(BOOKING_FLOW_USER_ID_COOKIE))

    const eventProps = {
      userId: sessionId,
      bookingFlowUserId: Cookies.get(BOOKING_FLOW_USER_ID_COOKIE),
      ...properties
    }

    if (abTestCampaign !== '0') {
      eventProps.abTestVariant =
        Number(Cookies.get(`${AB_TEST_DATA_VARIANT_COOKIE}${abTestCampaign}`)) === AbTestVariants.Control
          ? 'control_email_interstitial_pages'
          : 'test_email_interstitial_pages'
    }

    getAnalytics().instance?.track(eventName, eventProps, callback)
  }
}

const tempFuncToCatchMissingId = (eventName: string, bookingFlowUserId?: string) => {
  if (eventName === segmentEventsNames.BOOKING_FLOW_STARTED && !bookingFlowUserId) {
    console.error(`Booking flow started with booking flow user id NULL: ${bookingFlowUserId}`)
    const delay = 1000
    setTimeout(() => {
      console.error(
        `Booking flow started with booking flow user id NULL in ${delay}ms: ${Cookies.get(BOOKING_FLOW_USER_ID_COOKIE)}`
      )
    }, delay)
  }
}

export const segmentEventsNames = {
  HOME_PAGE_VIEW: 'Home Page View',
  ABA_THERAPY_VIEW: 'ABA Therapy View',
  AUTISM_DIAGNOSIS_VIEW: 'Autism Diagnosis View',
  OUR_PROVIDERS_VIEW: 'Our Providers View',
  FAQ_VIEW: 'FAQ View',
  LOCATIONS_STATE_VIEW: 'Locations State View',
  LEARNING_STUDIO_VIEW: 'Learning Studio View',
  COST_CALCULATOR_PERSONAL_INFO_VIEW: 'Cost Calculator Personal Info View',
  FOR_PROVIDERS_VIEW: 'For Providers View', // on the web site now it is For Providers
  CONTACT_US_VIEW: 'Contact Us View',
  FOR_PEDIATRICIANS_VIEW: 'For Pediatricians View',
  OUR_STORY_VIEW: 'Our Story View',
  LOCATIONS_MAIN_VIEW: 'Locations Main View',
  NBC_VIDEO_CLICK: 'NBC Video Click',
  PARENT360_LOG_IN: 'Parent360 Log In',
  FOR_PEDIATRICIANS_REFER_CLICK: 'For Pediatricians - Refer Click',
  CONTACT_INQUIRIES_CLICK: 'Contact Inquiries Click',
  FOR_PROVIDERS_APPLY_NOW: 'For Providers - Apply Now', // on the web site now it is For Providers
  CAREERS_VIEW: 'Careers View',
  CAREERS_EXPLORE_CLICK: 'Careers - Explore Click',
  LOCATIONS_CITY_VIEW: 'Locations City View',
  COST_CALCULATOR_COST_ESTIMATE_VIEW: 'Cost Calculator Cost Estimate View',
  IN_THE_PRESS_VIEW: 'In The Press View',
  IN_THE_PRESS_LINK_CLICK: 'In The Press Link Click',
  GET_STARTED_CLICK: 'Get Started Click',

  // SIGN UP RBT PAGE -> AMPLITUDE ON PROVIDER 360
  SIGN_UP_RBT_PAGE_VIEW: 'Sign Up RBT - Page View',
  SIGN_UP_RBT_FORM_COMPLETION: 'Sign Up RBT - Form Completion',

  // booking
  BOOKING_FLOW_STARTED: 'Booking Flow Started',
  BOOKING_FLOW_POSTAL_CODE_COMPLETED: 'Booking Flow Postal Code Completed',
  BOOKING_FLOW_POSTAL_CODE_OFFRAMP: 'Booking Flow Postal Code Offramp',
  BOOKING_FLOW_POSTAL_CODE_OFFRAMP_BUTTON: 'Booking Flow Postal Code Offramp Button Click',
  BOOKING_FLOW_SERVICE_SELECTION_COMPLETED: 'Booking Flow Service Selection Completed',
  BOOKING_FLOW_AGE_COMPLETED: 'Booking Flow Age Completed',
  BOOKING_FLOW_AGE_OFFRAMP: 'Booking Flow Age Offramp',
  BOOKING_FLOW_AGE_OFFRAMP_BUTTON: 'Booking Flow Age Offramp Button Click',
  BOOKING_FLOW_IS_PARENT_COMPLETED: 'Booking Flow Is Parent Completed',
  BOOKING_FLOW_LEGAL_GUARDIAN_COMPLETED: 'Booking Flow Legal Guardian Completed',
  BOOKING_FLOW_DIAGNOSIS_COMPLETED: 'Booking Flow Diagnosis Completed',
  BOOKING_FLOW_INSURANCE_COMPLETED: 'Booking Flow Insurance Completed',
  BOOKING_FLOW_CALL_REQUESTED: 'Booking Flow Call Requested',
  BOOKING_FLOW_CALL_SCHEDULED: 'Booking Flow Call Scheduled',
  BOOKING_FLOW_LEAD_CREATED: 'Booking Flow Lead Created',
  BOOKING_FLOW_COMPLETED: 'Booking Flow Completed',
  BOOKING_FLOW_ADDED_TO_CALENDAR: 'Booking Flow Add to calendar Scheduled Call',
  BOOKING_FLOW_NOT_QUALIFIED_VIEW: 'Not qualified view - booking flow',
  BOOKING_FLOW_NOT_QUALIFIED_CONTACT_SUBMIT: 'Booking Flow Not Qualified Contact Submit',
  BOOKING_FLOW_DIAGNOSIS_OFFRAMP: 'Booking Flow Diagnosis Offramp',
  BOOKING_FLOW_DIAGNOSIS_OFFRAMP_LINK: 'Booking Flow Diagnosis Offramp Get Started Link Click',
  BOOKING_FLOW_DIAGNOSIS_OFFRAMP_WITHOUT_SUPPORT: 'Booking Flow Diagnosis Offramp Without Support',
  BOOKING_FLOW_DIAGNOSIS_OFFRAMP_WITHOUT_SUPPORT_LINK:
    'Booking Flow Diagnosis Offramp Without Support Get Started Link Click',
  BOOKING_FLOW_LEGAL_GUARDIAN_OFFRAMP_VIEW: 'Booking Flow Legal Guardian Offramp View',
  BOOKING_FLOW_LEGAL_GUARDIAN_OFFRAMP_BUTTON: 'Booking Flow Legal Guardian Offramp Button Click',
  BOOKING_FLOW_CREATE_ACCONT_PAGE_VIEW: 'Booking Flow Create Accoung Page View',
  BOOKING_FLOW_CREATE_ACCOUNT_FORM_SUBMIT: 'Booking Flow Create Account Form Submit',

  // booking A/B test 1
  BOOKING_FLOW_STARTED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Started Email Interstitial Pages',
  BOOKING_FLOW_POSTAL_CODE_COMPLETED_EMAIL_INTERSTITIAL_PAGES:
    'Booking Flow Postal Code Completed Email Interstitial Pages',
  BOOKING_FLOW_EMAIL_COMPLETED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Email Completed Email Interstitial Pages',
  BOOKING_FLOW_AGE_COMPLETED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Age Completed Email Interstitial Pages',
  BOOKING_FLOW_PROGRESS_OUTCOMES_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Progress Outcomes Email Interstitial Pages',
  BOOKING_FLOW_DIAGNOSIS_COMPLETED_EMAIL_INTERSTITIAL_PAGES:
    'Booking Flow Diagnosis Completed Email Interstitial Pages',
  BOOKING_FLOW_SPEED_TO_CARE_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Speed to Care Email Interstitial Pages',
  BOOKING_FLOW_INSURANCE_COMPLETED_EMAIL_INTERSTITIAL_PAGES:
    'Booking Flow Insurance Completed Email Interstitial Pages',
  BOOKING_FLOW_THERAPISTS_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Therapists Email Interstitial Pages',
  BOOKING_FLOW_CALL_REQUESTED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Call Requested Email Interstitial Pages',
  BOOKING_FLOW_CALL_SCHEDULED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Call Scheduled Email Interstitial Pages',
  BOOKING_FLOW_COMPLETED_EMAIL_INTERSTITIAL_PAGES: 'Booking Flow Completed Email Interstitial Pages',
  BOOKING_FLOW_ADDED_TO_CALENDAR_EMAIL_INTERSTITIAL_PAGES:
    'Booking Flow Add to calendar Scheduled Call Email Interstitial Pages',
  NOT_QUALIFIED_VIEW_BOOKING_FLOW_EMAIL_INTERSTITIAL_PAGES:
    'Not qualified view - booking flow Email Interstitial Pages',
  BOOKING_FLOW_NOT_QUALIFIED_CONTACT_SUBMIT_EMAIL_INTERSTITIAL_PAGES:
    'Booking Flow Not Qualified Contact Submit Email Interstitial Pages'
}
